import type { ReactNode } from 'react';
import { ArrowRight } from 'lucide-react';

/**
 * Marketing CTA buttons matching the Studio mock chrome (.btn-primary /
 * .btn-ghost / .btn-ghost-dark). Hover states live in studio-cta-styles below,
 * injected once by StudioHero. Inline layout per repo convention.
 */
const baseBtn: React.CSSProperties = {
  borderRadius: '12px',
  fontWeight: 600,
  fontSize: '16px',
  display: 'inline-flex',
  alignItems: 'center',
  justifyContent: 'center',
  gap: '8px',
  cursor: 'pointer',
};

interface CtaProps {
  onClick: () => void;
  children: ReactNode;
  withArrow?: boolean;
  'data-testid'?: string;
}

export function PrimaryCta({ onClick, children, withArrow = true, ...rest }: CtaProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="studio-cta studio-cta--primary"
      data-testid={rest['data-testid']}
      style={{ ...baseBtn, background: 'var(--gradient-1)', color: '#fff', border: 'none', padding: '14px 28px' }}
    >
      {children}
      {withArrow && <ArrowRight size={18} />}
    </button>
  );
}

/** Ghost CTA for dark/navy backgrounds (light border). */
export function GhostCta({ onClick, children, withArrow = false, ...rest }: CtaProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="studio-cta studio-cta--ghost"
      data-testid={rest['data-testid']}
      style={{
        ...baseBtn,
        background: 'transparent',
        color: 'var(--text-primary)',
        border: '2px solid rgba(255,255,255,.28)',
        padding: '12px 26px',
      }}
    >
      {children}
      {withArrow && <ArrowRight size={18} />}
    </button>
  );
}

/** Ghost CTA for light backgrounds (slate border, dark text). */
export function GhostDarkCta({ onClick, children, withArrow = true, ...rest }: CtaProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      className="studio-cta studio-cta--ghost-dark"
      data-testid={rest['data-testid']}
      style={{
        ...baseBtn,
        background: 'transparent',
        color: 'var(--marketing-heading)',
        border: '1.5px solid #cbd5e1',
        padding: '12px 26px',
      }}
    >
      {children}
      {withArrow && <ArrowRight size={18} />}
    </button>
  );
}

/** Hover/transition rules for the CTAs — render once near the top of the page. */
export const studioCtaStyles = `
  .studio-cta { transition: all .2s ease; }
  .studio-cta--primary:hover { transform: translateY(-2px); box-shadow: 0 10px 40px rgba(20,184,166,.4); }
  .studio-cta--ghost:hover { border-color: var(--primary); background: rgba(20,184,166,.1); }
  .studio-cta--ghost-dark:hover { border-color: var(--primary-dark); background: rgba(20,184,166,.07); }
  @media (max-width: 767px) {
    .studio-cta-row {
      flex-direction: column !important;
      align-items: stretch !important;
      width: 100%;
      max-width: 360px;
      margin-left: auto;
      margin-right: auto;
    }
    .studio-cta-row .studio-cta {
      width: 100% !important;
      box-sizing: border-box;
    }
  }
  @media (prefers-reduced-motion: reduce) {
    .studio-cta, .studio-cta:hover { transition: none; transform: none; }
  }
`;
